import React, { useCallback, useEffect, useState } from 'react';
import {
    cancelVerify,
    fetchSyncOverview,
    fetchSyncSettings,
    fetchRowDiff,
    fetchVerifyProgress,
    pauseTableSync,
    reconcileTable,
    resumeTableSync,
    RowDiff,
    startVerify,
    SyncOverview as Overview,
    SyncSettings,
    triggerTableSync,
    updateSyncSettings,
    VerifyOptions,
    VerifyProgress,
} from '../../api/datasync';
import { ApiError } from '../../api/request';
import { useCan } from '../../store';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { formatTime } from '../../utils/time';
import { toast } from '../../utils/toast';
import { confirmDialog, alertDialog } from '../../utils/dialog';

// 落后超过该秒数标红 (5 分钟).
const LAG_WARN_SECONDS = 300;
const IMAGE_MODE_OPTS = [
    { value: 'passthrough', label: 'passthrough — 直通美服原链接 (国服 OSS 未就绪)' },
    { value: 'rewrite', label: 'rewrite — 下载原图搬到国服存储并改写链接' },
];

const STATUS_TAG: Record<string, string> = {
    idle: 'bg-slate-100 text-slate-600',
    running: 'bg-brand-50 text-brand-700',
    failed: 'bg-red-50 text-red-700',
    paused: 'bg-amber-50 text-amber-700',
};

const STATUS_LABEL: Record<string, string> = {
    idle: '正常',
    running: '同步中',
    failed: '失败',
    paused: '已暂停',
};

// 全量校验进度轮询间隔 (任务跑几分钟到几十分钟, 2s 足够跟手且不压后端).
const VERIFY_POLL_MS = 2000;

const VERIFY_STATUS_LABEL: Record<string, string> = {
    pending: '等待',
    running: '校验中',
    done: '完成',
    failed: '失败',
    canceled: '已中断',
};

const VERIFY_STATUS_TAG: Record<string, string> = {
    pending: 'bg-slate-100 text-slate-500',
    running: 'bg-brand-50 text-brand-700',
    done: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
    canceled: 'bg-amber-50 text-amber-700',
};

const VERIFY_DEFAULTS: VerifyOptions = { dryRun: false, migrateImages: true, imageBudget: 500 };

function SyncOverview() {
    const canManage = useCan('datasync', 'update');
    const [data, setData] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 同步设置弹窗
    const [settings, setSettings] = useState<SyncSettings | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // 行诊断
    const [diffTable, setDiffTable] = useState('');
    const [diffID, setDiffID] = useState('');
    const [diff, setDiff] = useState<RowDiff | null>(null);
    // 全量校验兜底
    const [verify, setVerify] = useState<VerifyProgress | null>(null);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyOpts, setVerifyOpts] = useState<VerifyOptions>(VERIFY_DEFAULTS);
    const [starting, setStarting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await fetchSyncOverview());
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // 校验进度: 进页面取一次; 任务在跑时每 2s 轮询, running 变 false 后 effect 自动清理定时器.
    const loadVerify = useCallback(async () => {
        try {
            setVerify(await fetchVerifyProgress());
        } catch {
            // 进度接口失败不打扰主视图 (概览自身有错误条)
        }
    }, []);

    useEffect(() => {
        loadVerify();
        // 顺带取一次设置: 校验弹窗要回显当前生效的图片域名白名单.
        fetchSyncSettings()
            .then(setSettings)
            .catch(() => undefined);
    }, [loadVerify]);

    const verifyRunning = !!verify?.running;
    useEffect(() => {
        if (!verifyRunning) return undefined;
        const timer = window.setInterval(() => {
            loadVerify();
            load(); // 校验会改行数, 概览跟着刷
        }, VERIFY_POLL_MS);
        return () => window.clearInterval(timer);
    }, [verifyRunning, loadVerify, load]);

    const doAction = async (fn: () => Promise<{ message: string }>) => {
        try {
            const res = await fn();
            toast(res.message || '操作成功');
            load();
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        }
    };

    const openSettings = async () => {
        try {
            setSettings(await fetchSyncSettings());
            setSettingsOpen(true);
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        }
    };

    const onSaveSettings = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const res = await updateSyncSettings(settings);
            toast(res.message || '已保存');
            setSettingsOpen(false);
            load();
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSaving(false);
        }
    };

    const onStartVerify = async () => {
        setStarting(true);
        try {
            const res = await startVerify(verifyOpts);
            toast(res.message || '已启动');
            setVerifyOpen(false);
            loadVerify();
        } catch (e) {
            alertDialog(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setStarting(false);
        }
    };

    const onCancelVerify = async () => {
        if (!await confirmDialog('中断全量校验? 当前表处理完即停; 已修好的行不会回滚, 再跑一轮可继续.')) return;
        await doAction(cancelVerify);
        loadVerify();
    };

    const onDiff = async (e: React.FormEvent) => {
        e.preventDefault();
        const id = Number(diffID);
        if (!diffTable.trim() || !id) return;
        try {
            setDiff(await fetchRowDiff(diffTable.trim(), id));
        } catch (err) {
            alertDialog(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
        }
    };

    if (data && data.siteRole !== 'replica') {
        return (
            <div className="card p-10 text-center text-slate-500">
                <div className="text-3xl mb-3">🌎</div>
                <div className="font-semibold text-slate-700 mb-1">当前为源站 (master)</div>
                <div className="text-sm">本站是数据源, 无同步任务; 同步状态请到国服镜像站查看.</div>
            </div>
        );
    }

    const tables = data?.tables || [];
    const failedCnt = tables.filter((t) => t.status === 'failed').length;
    const maxLag = tables.reduce((m, t) => (t.lagSeconds > m ? t.lagSeconds : m), -1);

    return (
        <div className="space-y-4">
            {canManage && (
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                            setVerifyOpts(VERIFY_DEFAULTS);
                            setVerifyOpen(true);
                        }}
                        disabled={verifyRunning}
                        title="逐行比对两端全部字段, 以美服为准修复国服 (图片有图不改、缺图从美服搬)"
                    >
                        {verifyRunning ? '🔍 校验进行中…' : '🔍 全量校验'}
                    </button>
                    <button type="button" className="btn-ghost" onClick={openSettings}>
                        ⚙️ 同步设置
                    </button>
                </div>
            )}
            {/* 汇总卡 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card p-4">
                    <div className="text-xs text-slate-400 mb-1">同步表数</div>
                    <div className="text-xl font-semibold">{tables.length}</div>
                </div>
                <div className="card p-4">
                    <div className="text-xs text-slate-400 mb-1">最大落后</div>
                    <div className={`text-xl font-semibold ${maxLag > LAG_WARN_SECONDS ? 'text-red-600' : ''}`}>
                        {maxLag < 0 ? '-' : `${maxLag}s`}
                    </div>
                </div>
                <div className="card p-4">
                    <div className="text-xs text-slate-400 mb-1">失败表数</div>
                    <div className={`text-xl font-semibold ${failedCnt ? 'text-red-600' : ''}`}>{failedCnt}</div>
                </div>
                <div className="card p-4">
                    <div className="text-xs text-slate-400 mb-1">待补图片 (图片模式: {data?.imageMode || '-'})</div>
                    <div className={`text-xl font-semibold ${data?.pendingImages ? 'text-amber-600' : ''}`}>
                        {data?.pendingImages ?? 0}
                    </div>
                </div>
            </div>

            {data?.sourceError && (
                <div className="card p-3 text-sm text-red-700 bg-red-50">源站不可达: {data.sourceError}</div>
            )}
            {error && <div className="card p-3 text-sm text-red-700 bg-red-50">{error}</div>}

            {/* 行诊断 */}
            <form onSubmit={onDiff} className="card p-3 flex items-center gap-2 text-sm flex-wrap">
                <span className="text-slate-500">行诊断:</span>
                <input
                    className="inp w-48"
                    placeholder="表名, 如 material"
                    value={diffTable}
                    onChange={(e) => setDiffTable(e.target.value)}
                />
                <input
                    className="inp w-32"
                    placeholder="行 id"
                    value={diffID}
                    onChange={(e) => setDiffID(e.target.value)}
                />
                <button type="submit" className="btn-ghost">
                    对比两端
                </button>
                {diff && (
                    <span className="text-slate-600">
                        美服: {diff.sourceError ? `不可达(${diff.sourceError})` : diff.sourceExists
                            ? `存在${diff.sourceDeleted ? '(已删)' : ''} updatedAt=${diff.sourceUpdatedAt}`
                            : '不存在'}
                        {' · '}
                        国服: {diff.localExists
                            ? `存在${diff.localDeleted ? '(已删)' : ''} updatedAt=${diff.localUpdatedAt}`
                            : '不存在'}
                    </span>
                )}
            </form>

            {/* 全量校验进度 */}
            {verify && (verify.running || verify.startedAt > 0) && (
                <div className="card p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-semibold text-slate-700">
                            全量校验
                            <span className="ml-2 text-xs font-normal text-slate-500">
                                {verify.options?.dryRun ? '试跑 (只报差异)' : '校验并修复'}
                                {verify.options?.migrateImages ? ' · 缺图搬运' : ' · 不搬图'}
                                {verify.startedAt ? ` · 开始 ${formatTime(verify.startedAt)}` : ''}
                                {!verify.running && verify.endedAt ? ` · 结束 ${formatTime(verify.endedAt)}` : ''}
                                {verify.canceled ? ' · 已中断' : ''}
                            </span>
                        </div>
                        {canManage && verify.running && (
                            <button type="button" className="btn-danger-text" onClick={onCancelVerify}>
                                中断
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
                        {[
                            { k: '已比对', v: verify.totals?.checked ?? 0, cls: '' },
                            { k: '有差异', v: verify.totals?.mismatched ?? 0, cls: verify.totals?.mismatched ? 'text-amber-600' : '' },
                            { k: '已修复', v: verify.totals?.fixed ?? 0, cls: 'text-emerald-700' },
                            { k: '本地缺失', v: verify.totals?.missingLocal ?? 0, cls: '' },
                            { k: '多余软删', v: verify.totals?.extraLocal ?? 0, cls: '' },
                            { k: '已搬图', v: verify.totals?.imagesMigrated ?? 0, cls: 'text-emerald-700' },
                        ].map((x) => (
                            <div key={x.k}>
                                <div className="text-[11px] text-slate-400">{x.k}</div>
                                <div className={`text-lg font-semibold ${x.cls}`}>{x.v}</div>
                            </div>
                        ))}
                    </div>
                    {!!verify.totals?.imagesPending && (
                        <div className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                            还有 {verify.totals.imagesPending} 张图未补上
                            {verify.imageBudgetLeft <= 0 && verify.options?.migrateImages
                                ? ' — 本次搬图预算已用尽, 再点一次全量校验可继续 (已搬的不会重搬)'
                                : verify.options?.migrateImages
                                  ? ' — 搬运失败, 详情见「同步记录」里 imageStatus=pending 的行'
                                  : ' — 本次未勾选搬图, 勾上「缺图从美服搬运」再跑一轮即可补齐'}
                        </div>
                    )}
                    {verify.error && (
                        <div className="text-xs text-red-700 bg-red-50 rounded p-2">{verify.error}</div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr>
                                    <th className="th">表</th>
                                    <th className="th">状态</th>
                                    <th className="th">已比对</th>
                                    <th className="th">差异</th>
                                    <th className="th">已修</th>
                                    <th className="th">缺失</th>
                                    <th className="th">多余</th>
                                    <th className="th">搬图</th>
                                    <th className="th">待补图</th>
                                    <th className="th">耗时</th>
                                    <th className="th">错误</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(verify.tables || [])
                                    .filter((t) => t.status !== 'pending' || verify.running)
                                    .map((t) => (
                                        <tr key={t.table}>
                                            <td className="td font-mono">{t.table}</td>
                                            <td className="td">
                                                <span className={`tag ${VERIFY_STATUS_TAG[t.status] || ''}`}>
                                                    {VERIFY_STATUS_LABEL[t.status] || t.status}
                                                </span>
                                            </td>
                                            <td className="td">{t.checked}</td>
                                            <td className={`td ${t.mismatched ? 'text-amber-600 font-semibold' : ''}`}>
                                                {t.mismatched}
                                            </td>
                                            <td className="td">{t.fixed}</td>
                                            <td className="td">{t.missingLocal}</td>
                                            <td className="td">{t.extraLocal}</td>
                                            <td className="td">{t.imagesMigrated}</td>
                                            <td className={`td ${t.imagesPending ? 'text-amber-600' : ''}`}>
                                                {t.imagesPending}
                                            </td>
                                            <td className="td">{t.costMs ? `${t.costMs}ms` : '-'}</td>
                                            <td className="td max-w-[200px] truncate text-red-600" title={t.error}>
                                                {t.error || '-'}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 表格 */}
            <div className="card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="th">表</th>
                            <th className="th">状态</th>
                            <th className="th">国服行数</th>
                            <th className="th">美服行数</th>
                            <th className="th">水位时间</th>
                            <th className="th">落后</th>
                            <th className="th">最近成功</th>
                            <th className="th">最近对账</th>
                            <th className="th">错误</th>
                            {canManage && <th className="th">操作</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !tables.length && (
                            <tr>
                                <td className="td text-slate-400" colSpan={10}>
                                    加载中...
                                </td>
                            </tr>
                        )}
                        {tables.map((t) => (
                            <tr key={t.table}>
                                <td className="td font-mono">{t.table}</td>
                                <td className="td">
                                    <span className={`tag ${STATUS_TAG[t.status] || ''}`}>
                                        {STATUS_LABEL[t.status] || t.status}
                                        {t.failStreak > 0 ? ` ×${t.failStreak}` : ''}
                                    </span>
                                </td>
                                <td className="td">{t.localRows}</td>
                                <td className="td">
                                    {t.sourceRows < 0 ? '-' : t.sourceRows}
                                    {t.sourceRows >= 0 && t.sourceRows !== t.localRows && (
                                        <span className="text-amber-600 ml-1" title="两端行数不一致 (本地新增行/同步进行中亦会造成差异)">
                                            ⚠
                                        </span>
                                    )}
                                </td>
                                <td className="td">{t.watermark ? formatTime(t.watermark) : '-'}</td>
                                <td className={`td ${t.lagSeconds > LAG_WARN_SECONDS ? 'text-red-600 font-semibold' : ''}`}>
                                    {t.lagSeconds < 0 ? '-' : `${t.lagSeconds}s`}
                                </td>
                                <td className="td">{t.lastOkAt ? formatTime(t.lastOkAt) : '-'}</td>
                                <td className="td">{t.lastReconcileAt ? formatTime(t.lastReconcileAt) : '-'}</td>
                                <td className="td max-w-[240px] truncate text-red-600" title={t.lastError}>
                                    {t.lastError || '-'}
                                </td>
                                {canManage && (
                                    <td className="td whitespace-nowrap">
                                        <button
                                            type="button"
                                            className="btn-ghost mr-1"
                                            onClick={() => doAction(() => triggerTableSync(t.table))}
                                        >
                                            同步
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-ghost mr-1"
                                            onClick={() => doAction(() => reconcileTable(t.table))}
                                        >
                                            对账
                                        </button>
                                        {t.status === 'paused' ? (
                                            <button
                                                type="button"
                                                className="btn-ghost"
                                                onClick={() => doAction(() => resumeTableSync(t.table))}
                                            >
                                                恢复
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                onClick={() => doAction(() => pauseTableSync(t.table))}
                                            >
                                                暂停
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                        {!loading && !tables.length && !error && (
                            <tr>
                                <td className="td text-slate-400" colSpan={10}>
                                    暂无数据
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {verifyOpen && (
                <Modal
                    open
                    title="全量校验 (兜底)"
                    subtitle="逐行比对两端全部字段, 以美服为准修复国服 — 定时同步之外的兜底闸门"
                    width={560}
                    onClose={() => setVerifyOpen(false)}
                >
                    <div className="space-y-4 text-sm">
                        <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 space-y-1">
                            <div>
                                · 定时同步只看 <span className="font-mono">updated_at</span> 水位, 对账只比 id 集合;
                                本操作把两端每一行的每一列都比一遍, 漏同步/被改花的行都能兜住.
                            </div>
                            <div>
                                · 图片: 国服已有图的<span className="font-semibold">不动</span>;
                                缺图的从美服下载并上传到国服存储, 再改写链接. 因此校验后两端除图片地址外完全一致.
                            </div>
                            <div>· 全量拉取跨境 + 搬图较慢, 任务在后台跑, 可随时中断; 重复跑是幂等的.</div>
                        </div>
                        <label className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!verifyOpts.dryRun}
                                onChange={(e) =>
                                    setVerifyOpts({
                                        ...verifyOpts,
                                        dryRun: e.target.checked,
                                        // 试跑不产生任何外部写入, 搬图一并关掉
                                        migrateImages: e.target.checked ? false : verifyOpts.migrateImages,
                                    })
                                }
                            />
                            <span>
                                只试跑, 不写库
                                <div className="text-[11px] text-slate-400">
                                    先看差异规模再决定要不要真修; 试跑不搬图、不改任何数据.
                                </div>
                            </span>
                        </label>
                        <label className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!verifyOpts.migrateImages}
                                disabled={!!verifyOpts.dryRun}
                                onChange={(e) => setVerifyOpts({ ...verifyOpts, migrateImages: e.target.checked })}
                            />
                            <span>
                                缺图从美服搬运到国服存储
                                <div className="text-[11px] text-slate-400">
                                    仅对「同步设置」里白名单域名内的图片生效
                                    {settings?.imageHosts ? ` (当前: ${settings.imageHosts})` : ''}.
                                </div>
                            </span>
                        </label>
                        <div>
                            <label className="field-label">本次搬图上限</label>
                            <input
                                className="inp"
                                type="number"
                                value={verifyOpts.imageBudget ?? 500}
                                disabled={!verifyOpts.migrateImages}
                                onChange={(e) => setVerifyOpts({ ...verifyOpts, imageBudget: Number(e.target.value) })}
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                防止一次跑太久. 用尽后剩余缺图会标成待补, 再跑一轮继续 (已搬的不重搬).
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button type="button" className="btn-ghost" onClick={() => setVerifyOpen(false)} disabled={starting}>
                                取消
                            </button>
                            <button type="button" className="btn-primary" onClick={onStartVerify} disabled={starting}>
                                {starting ? '启动中…' : verifyOpts.dryRun ? '开始试跑' : '开始校验并修复'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {settingsOpen && settings && (
                <Modal open title="同步设置" subtitle="保存后下一轮同步生效, 无需重启服务" width={520} onClose={() => setSettingsOpen(false)}>
                    <div className="space-y-4 text-sm">
                        <div>
                            <label className="field-label">增量轮询间隔 (秒, 10~3600)</label>
                            <input
                                className="inp"
                                type="number"
                                value={settings.intervalSeconds}
                                onChange={(e) => setSettings({ ...settings, intervalSeconds: Number(e.target.value) })}
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                美服编辑后国服最迟这么多秒可见; 太小会放大对美服的跨境查询压力.
                            </div>
                        </div>
                        <div>
                            <label className="field-label">每页拉取行数 (50~1000)</label>
                            <input
                                className="inp"
                                type="number"
                                value={settings.pageSize}
                                onChange={(e) => setSettings({ ...settings, pageSize: Number(e.target.value) })}
                            />
                        </div>
                        <div>
                            <label className="field-label">图片模式</label>
                            <SearchSelect
                                className="w-full"
                                value={settings.imageMode}
                                onChange={(v) => setSettings({ ...settings, imageMode: v })}
                                options={IMAGE_MODE_OPTS}
                            />
                        </div>
                        <div>
                            <label className="field-label">待搬图片域名白名单 (逗号分隔)</label>
                            <input
                                className="inp"
                                value={settings.imageHosts}
                                onChange={(e) => setSettings({ ...settings, imageHosts: e.target.value })}
                                placeholder="storage-us.atomm.com,image-res.xtool.com"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                只有命中这里的域名才会被搬到国服存储. 配漏等于搬图静默不生效 —— 美服图片
                                散落在 storage-us.atomm.com / image-res.xtool.com 等多个域名, 上线前请核对.
                                留空则回落环境变量 SYNC_IMAGE_HOSTS.
                            </div>
                        </div>
                        <div>
                            <label className="field-label">同步记录保留天数 (7~3650)</label>
                            <input
                                className="inp"
                                type="number"
                                value={settings.recordRetainDays}
                                onChange={(e) => setSettings({ ...settings, recordRetainDays: Number(e.target.value) })}
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                超期的行级同步记录会被清理 (搬图未完成的不清); 只影响审计追溯深度.
                            </div>
                        </div>
                        {!settings.fromDb && (
                            <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
                                当前展示的是环境变量默认值 (尚未在页面保存过); 保存后以页面设置为准.
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button type="button" className="btn-ghost" onClick={() => setSettingsOpen(false)} disabled={saving}>
                                取消
                            </button>
                            <button type="button" className="btn-primary" onClick={onSaveSettings} disabled={saving}>
                                {saving ? '保存中…' : '保存'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default SyncOverview;
